import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import Api, { getApiUrl } from '../../services/api';
import MessageModal from '../common/MessageModal';
import { useAuth } from '../../context/AuthContext';

const Profile = ({ user, onLogout: _onLogout }) => {
  const { updateUser } = useAuth();
  const [stats, setStats] = useState({
    total: 0,
    completed: 0,
    pending: 0,
    totalSpent: 0
  });
  const [uploadingPicture, setUploadingPicture] = useState(false);
  const [profilePicturePreview, setProfilePicturePreview] = useState(null);
  const fileInputRef = useRef(null);
  const [editingField, setEditingField] = useState(null); // 'username', 'email', 'phone', 'pincode', 'address', null
  const [editFormData, setEditFormData] = useState({ username: '', email: '', phone: '', pincode: '', address: '' });
  const [updating, setUpdating] = useState(false);
  const [messageModal, setMessageModal] = useState({ show: false, message: '', type: 'info', title: 'Message' });

  // Debug: Log messageModal changes
  useEffect(() => {
    console.log('📢 messageModal state changed:', messageModal);
  }, [messageModal]);

  // Auto-hide toast message after 2.5 seconds (non-blocking, no user interaction required)
  useEffect(() => {
    if (!messageModal.show) return undefined;
    console.log('⏰ Starting auto-hide timer for toast...');
    const timeoutId = setTimeout(() => {
      console.log('⏰ Auto-hiding toast after 2.5s');
      setMessageModal(prev => ({ ...prev, show: false }));
    }, 2500); // 2.5 seconds - perfect balance
    return () => {
      console.log('🧹 Cleaning up toast timer');
      clearTimeout(timeoutId);
    };
  }, [messageModal.show]);

  useEffect(() => {
    fetchUserRides();
  }, [user]);

  const fetchUserRides = async () => {
    try {
      const token = localStorage.getItem('uttaapp_token');
      const response = await axios.get(Api.Booking.myBookings, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.data.success && response.data.bookings) {
        const bookings = response.data.bookings;
        
        // Calculate statistics
        const total = bookings.length;
        const completed = bookings.filter(b => b.status === 'completed' || b.status === 'accepted').length;
        const pending = bookings.filter(b => b.status === 'pending' || b.status === 'negotiating').length;
        const totalSpent = bookings
          .filter(b => b.fare && (b.status === 'completed' || b.paymentStatus === 'completed'))
          .reduce((sum, b) => sum + (parseFloat(b.fare) || 0), 0);
        
        setStats({ total, completed, pending, totalSpent });
      }
    } catch (error) {
      console.error('Error fetching user rides:', error);
    }
  };

  const getProfileImageUrl = (profilePicture) => {
    if (!profilePicture) return null;
    // If it's a full URL, return as is
    if (profilePicture.startsWith('http')) return profilePicture;
    // Otherwise, construct backend URL using centralized API helper
    const baseUrl = getApiUrl();
    return `${baseUrl}/${profilePicture.replace(/\\/g, '/')}`;
  };

  const handleProfilePictureClick = () => {
    fileInputRef.current?.click();
  };

  const handleProfilePictureChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setMessageModal({ show: true, message: 'Please select an image file', type: 'error', title: 'Invalid File' });
      return;
    }

    // Show preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setProfilePicturePreview(reader.result);
    };
    reader.readAsDataURL(file);

    // Upload profile picture
    setUploadingPicture(true);
    try {
      const formData = new FormData();
      formData.append('profilePicture', file);

      const token = localStorage.getItem('uttaapp_token');
      if (!token) {
        throw new Error('Authentication token not found. Please login again.');
      }

      console.log('📤 Uploading profile picture to:', Api.User.updateProfilePicture);
      console.log('📦 File details:', { name: file.name, type: file.type, size: file.size });
      
      const response = await axios.put(Api.User.updateProfilePicture, formData, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      console.log('📥 Profile picture upload response:', response.data);

      if (response.data.success) {
        // Get the updated profile picture from response
        const updatedProfilePicture = response.data.profilePicture || response.data.user?.profilePicture || response.data.data?.profilePicture;
        
        if (!updatedProfilePicture) {
          console.warn('⚠️ Profile picture updated but no profilePicture in response:', response.data);
          throw new Error('Profile picture updated but server did not return the new picture path');
        }

        // Update user context with new profile picture
        const updatedUser = { ...user, profilePicture: updatedProfilePicture };
        
        if (updateUser) {
          updateUser(updatedUser);
          console.log('✅ User context updated with new profile picture:', updatedProfilePicture);
        } else {
          console.warn('⚠️ updateUser function not available, updating localStorage directly');
          // Fallback: Update localStorage directly if updateUser is not available
          localStorage.setItem('uttaapp_user', JSON.stringify(updatedUser));
        }
        
        // Also ensure localStorage is updated (double-check)
        try {
          const storedUser = localStorage.getItem('uttaapp_user');
          if (storedUser) {
            const parsedUser = JSON.parse(storedUser);
            parsedUser.profilePicture = updatedProfilePicture;
            localStorage.setItem('uttaapp_user', JSON.stringify(parsedUser));
            console.log('✅ localStorage also updated with new profile picture');
          }
        } catch (localStorageError) {
          console.error('❌ Error updating localStorage:', localStorageError);
        }
        
        // Show non-blocking toast notification
        console.log('✅ Profile picture updated successfully in database!');
        const toastData = { 
          show: true, 
          message: 'Profile picture updated successfully!', 
          type: 'success', 
          title: '' // No title for cleaner look
        };
        setMessageModal(toastData);
        setProfilePicturePreview(null);
      } else {
        throw new Error(response.data.message || 'Failed to update profile picture');
      }
    } catch (error) {
      console.error('Error updating profile picture:', error);
      setMessageModal({ show: true, message: error.response?.data?.message || 'Failed to update profile picture. Please try again.', type: 'error', title: 'Error' });
      setProfilePicturePreview(null);
    } finally {
      setUploadingPicture(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  // Handle edit field
  const handleEditClick = (field) => {
    setEditingField(field);
    setEditFormData({
      username: user?.username || '',
      email: user?.email || '',
      phone: user?.phone || '',
      pincode: user?.pincode || '',
      address: user?.address || ''
    });
  };

  // Handle cancel edit
  const handleCancelEdit = () => {
    setEditingField(null);
    setEditFormData({ username: '', email: '', phone: '', pincode: '', address: '' });
  };

  // Handle save edit
  const handleSaveEdit = async () => {
    try {
      setUpdating(true);
      const token = localStorage.getItem('uttaapp_token');
      const updateData = {};
      
      if (editingField === 'username' && editFormData.username) {
        updateData.username = editFormData.username;
      } else if (editingField === 'pincode' && editFormData.pincode) {
        updateData.pincode = editFormData.pincode;
      } else if (editingField === 'address' && editFormData.address) {
        updateData.address = editFormData.address;
      }

      if (Object.keys(updateData).length === 0) {
        setMessageModal({ show: true, message: 'Please enter a value', type: 'warning', title: 'Validation Error' });
        setUpdating(false);
        return;
      }

      const response = await axios.put(Api.User.updateProfile, updateData, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.data.success) {
        setMessageModal({ show: true, message: 'Profile updated successfully!', type: 'success', title: 'Success' });
        setEditingField(null);
        // Update user context with merged data
        if (updateUser && response.data.user) {
          // Merge updated fields with existing user data to preserve all fields
          const updatedUser = {
            ...user, // Keep all existing user fields
            ...response.data.user // Override with updated fields from backend
          };
          updateUser(updatedUser);
        }
        // Don't reload - the state update will reflect changes immediately
      } else {
        throw new Error(response.data.message || 'Failed to update profile');
      }
    } catch (error) {
      console.error('Error updating profile:', error);
      setMessageModal({ show: true, message: error.response?.data?.message || 'Failed to update profile. Please try again.', type: 'error', title: 'Error' });
    } finally {
      setUpdating(false);
    }
  };

  return (
    <>
      <style>{`
        @keyframes slideDown {
          from {
            opacity: 0;
            transform: translate(-50%, -20px);
          }
          to {
            opacity: 1;
            transform: translate(-50%, 0);
          }
        }
        .animate-slideDown {
          animation: slideDown 0.3s ease-out forwards;
        }
      `}</style>
      <div className="bg-gray-50 pb-20 dark:bg-slate-950 md:relative md:overflow-hidden md:bg-[linear-gradient(180deg,#edf7f2_0%,#f8fbf9_35%,#f4efe7_100%)] md:pb-0 md:dark:bg-[linear-gradient(180deg,#081311_0%,#0b1a17_38%,#111827_100%)]" style={{ paddingBottom: '80px' }}>
      <div className="pointer-events-none absolute inset-0 hidden md:block">
        <div className="absolute left-[-8%] top-8 h-72 w-72 rounded-full bg-[#a6ddd0]/35 blur-3xl dark:bg-[#0f766e]/25" />
        <div className="absolute right-[-6%] top-20 h-80 w-80 rounded-full bg-[#efe3d2]/80 blur-3xl dark:bg-[#1f2937]/80" />
        <div className="absolute bottom-20 left-1/3 h-64 w-64 rounded-full bg-[#d8efe7]/50 blur-3xl dark:bg-[#0b5f58]/20" />
      </div>
      <div className="px-4 pt-4 md:hidden">
        <div className="px-1">
          <div className="flex items-center justify-between gap-3">
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-[0.24em] text-[#0f766e] dark:text-emerald-300">Account Center</p>
              <h1 className="mt-2 text-[2rem] font-black leading-[0.95] tracking-[-0.05em] text-slate-950 dark:text-slate-100">
                Profile
              </h1>
            </div>
            <div className="rounded-full bg-[#e8f5f2] px-3 py-2 text-[11px] font-semibold text-[#0f766e] dark:bg-emerald-500/12 dark:text-emerald-300">
              Verified
            </div>
          </div>

          <div className="mt-4 overflow-hidden rounded-[30px] bg-[#111827] px-4 py-4 text-white shadow-[0_26px_50px_-34px_rgba(15,23,42,0.58)] dark:bg-slate-900">
            <div className="flex items-center gap-4">
              <button
                type="button"
                onClick={handleProfilePictureClick}
                className="relative flex h-20 w-20 flex-shrink-0 items-center justify-center overflow-hidden rounded-[26px] bg-white/12 ring-1 ring-white/10"
              >
                {profilePicturePreview ? (
                  <img
                    src={profilePicturePreview}
                    alt="Profile Preview"
                    className="h-full w-full object-cover"
                  />
                ) : getProfileImageUrl(user?.profilePicture) ? (
                  <img
                    src={getProfileImageUrl(user.profilePicture)}
                    alt="Profile"
                    className="h-full w-full object-cover"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.nextSibling.style.display = 'flex';
                    }}
                  />
                ) : null}
                <div
                  className="h-full w-full items-center justify-center bg-white/10"
                  style={{ display: (profilePicturePreview || getProfileImageUrl(user?.profilePicture)) ? 'none' : 'flex' }}
                >
                  <span className="text-3xl font-black text-white">
                    {user?.username ? user.username.charAt(0).toUpperCase() : 'U'}
                  </span>
                </div>
                {uploadingPicture && (
                  <div className="absolute inset-0 flex items-center justify-center bg-black/45">
                    <div className="h-7 w-7 animate-spin rounded-full border-b-2 border-white"></div>
                  </div>
                )}
              </button>

              <div className="min-w-0 flex-1">
                <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-white/45">Primary Account</p>
                <h2 className="mt-2 truncate text-[1.35rem] font-black leading-tight text-white">
                  {user?.username || 'User'}
                </h2>
                <p className="mt-1 truncate text-sm text-white/68">
                  {user?.email || 'user@example.com'}
                </p>
                <p className="mt-3 text-xs text-white/50">
                  Manage your details and travel activity.
                </p>
              </div>
            </div>

            <div className="mt-4 flex gap-2 overflow-x-auto pb-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
              <div className="min-w-[102px] rounded-[20px] bg-white/10 px-3 py-3 ring-1 ring-white/10">
                <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-white/45">Rides</div>
                <div className="mt-2 text-lg font-black text-white">{stats.total}</div>
              </div>
              <div className="min-w-[102px] rounded-[20px] bg-white px-3 py-3 text-slate-950 dark:bg-slate-800 dark:text-slate-100 dark:ring-1 dark:ring-slate-700">
                <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-400 dark:text-slate-500">Done</div>
                <div className="mt-2 text-lg font-black">{stats.completed}</div>
              </div>
              <div className="min-w-[128px] rounded-[20px] bg-[#0f766e] px-3 py-3 text-white">
                <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-white/50">Spent</div>
                <div className="mt-2 text-base font-black">Rs.{stats.totalSpent.toFixed(0)}</div>
              </div>
            </div>
          </div>

          <div className="mt-4 grid grid-cols-2 gap-3">
            <button
              type="button"
              onClick={handleProfilePictureClick}
              className="rounded-[22px] bg-white px-4 py-4 text-left shadow-[0_18px_35px_-28px_rgba(15,23,42,0.15)] dark:bg-slate-900 dark:ring-1 dark:ring-slate-800"
            >
              <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-400 dark:text-slate-500">Quick Action</div>
              <div className="mt-2 text-sm font-bold text-slate-950 dark:text-slate-100">Change Photo</div>
            </button>
            <button
              type="button"
              onClick={() => handleEditClick('username')}
              className="rounded-[22px] bg-[#e8f5f2] px-4 py-4 text-left shadow-[0_18px_35px_-28px_rgba(15,118,110,0.18)] dark:bg-emerald-950/60 dark:ring-1 dark:ring-emerald-800/60"
            >
              <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-[#0f766e] dark:text-emerald-200">Quick Action</div>
              <div className="mt-2 text-sm font-bold text-slate-950 dark:text-emerald-50">Edit Name</div>
            </button>
          </div>
        </div>
      </div>
      {/* Profile Header */}
      <div className="hidden bg-gradient-to-r from-primary-500 to-primary-600 py-8 md:block md:bg-transparent md:py-8">
        <div className="relative z-10 mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 md:max-w-7xl">
          <div className="text-center md:overflow-hidden md:rounded-[40px] md:border md:border-[#d6ebe4] md:bg-[linear-gradient(135deg,#0b5f58_0%,#117a72_45%,#f8f4ed_45%,#f3ece2_100%)] md:px-8 md:py-8 md:text-left md:shadow-[0_40px_90px_-60px_rgba(15,23,42,0.45)] md:dark:border-slate-800 md:dark:bg-[linear-gradient(135deg,#0b3f3b_0%,#115e59_42%,#111827_42%,#0f172a_100%)] md:dark:shadow-[0_40px_90px_-60px_rgba(0,0,0,0.72)] lg:px-10 lg:py-10">
            <div className="md:grid md:items-center md:gap-8 lg:grid-cols-[0.88fr,1.12fr]">
            <div className="relative inline-block mb-4 md:mb-0 md:block">
              <div 
                onClick={handleProfilePictureClick}
                className="mx-auto flex h-20 w-20 cursor-pointer items-center justify-center overflow-hidden rounded-full bg-white transition-all group relative hover:ring-4 hover:ring-white/50 dark:bg-slate-900 md:h-36 md:w-36 md:rounded-[34px] md:ring-8 md:ring-white/12 md:dark:ring-white/6"
              >
                {profilePicturePreview ? (
                  <img 
                    src={profilePicturePreview} 
                    alt="Profile Preview" 
                    className="h-full w-full object-cover rounded-full md:rounded-[34px]"
                  />
                ) : getProfileImageUrl(user?.profilePicture) ? (
                  <img 
                    src={getProfileImageUrl(user.profilePicture)} 
                    alt="Profile" 
                    className="h-full w-full object-cover rounded-full md:rounded-[34px]"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.nextSibling.style.display = 'flex';
                    }}
                  />
                ) : null}
                <div 
                  className="w-full h-full bg-gradient-to-br from-primary-400 to-primary-600 flex items-center justify-center"
                  style={{ display: (profilePicturePreview || getProfileImageUrl(user?.profilePicture)) ? 'none' : 'flex' }}
                >
                  <span className="text-2xl md:text-3xl font-bold text-white">
                    {user?.username ? user.username.charAt(0).toUpperCase() : 'U'}
                  </span>
                </div>
                {/* Edit overlay */}
                <div className="absolute inset-0 flex items-center justify-center rounded-full bg-black/50 opacity-0 transition-opacity group-hover:opacity-100 md:rounded-[34px]">
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                  </svg>
                </div>
                {uploadingPicture && (
                  <div className="absolute inset-0 flex items-center justify-center rounded-full bg-black/50 md:rounded-[34px]">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white"></div>
                  </div>
                )}
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleProfilePictureChange}
                className="hidden"
              />
            </div>
            <div>
            <div className="hidden md:inline-flex md:items-center md:rounded-full md:border md:border-white/12 md:bg-white/12 md:px-4 md:py-2 md:text-[11px] md:font-semibold md:uppercase md:tracking-[0.24em] md:text-white/72 md:dark:border-white/8 md:dark:bg-white/5 md:dark:text-white/55">
              Account overview
            </div>
            <h1 className="mb-2 text-2xl font-bold text-white dark:text-slate-100 md:mt-5 md:text-[4rem] md:font-extrabold md:leading-[0.92] md:tracking-[-0.06em] md:text-slate-950 md:dark:text-white">
              {user?.username || 'User'}
            </h1>
            <p className="text-lg text-primary-100 md:inline-flex md:rounded-full md:bg-white/82 md:px-5 md:py-3 md:text-base md:font-medium md:text-slate-700 md:shadow-[0_18px_45px_-30px_rgba(15,23,42,0.38)] md:dark:bg-slate-900/88 md:dark:text-slate-200 md:dark:shadow-[0_18px_45px_-30px_rgba(0,0,0,0.75)]">
              {user?.email || 'user@example.com'}
            </p>
            <p className="mt-2 text-sm text-primary-200 dark:text-slate-400 md:mt-4 md:max-w-xl md:text-sm md:text-slate-500 md:dark:text-slate-400">
              Click on profile picture to update
            </p>
            <div className="mt-5 hidden md:grid md:grid-cols-3 md:gap-3">
              <div className="rounded-[24px] bg-white/88 px-4 py-4 shadow-[0_24px_55px_-38px_rgba(15,23,42,0.4)] dark:bg-slate-900/88 dark:shadow-[0_24px_55px_-38px_rgba(0,0,0,0.75)]">
                <div className="text-[11px] uppercase tracking-[0.2em] text-slate-400 dark:text-slate-500">Total rides</div>
                <div className="mt-1 text-2xl font-bold text-slate-950 dark:text-slate-100">{stats.total}</div>
              </div>
              <div className="rounded-[24px] bg-white/88 px-4 py-4 shadow-[0_24px_55px_-38px_rgba(15,23,42,0.4)] dark:bg-slate-900/88 dark:shadow-[0_24px_55px_-38px_rgba(0,0,0,0.75)]">
                <div className="text-[11px] uppercase tracking-[0.2em] text-slate-400 dark:text-slate-500">Completed</div>
                <div className="mt-1 text-2xl font-bold text-[#0f766e]">{stats.completed}</div>
              </div>
              <div className="rounded-[24px] bg-white/88 px-4 py-4 shadow-[0_24px_55px_-38px_rgba(15,23,42,0.4)] dark:bg-slate-900/88 dark:shadow-[0_24px_55px_-38px_rgba(0,0,0,0.75)]">
                <div className="text-[11px] uppercase tracking-[0.2em] text-slate-400 dark:text-slate-500">Spent</div>
                <div className="mt-1 text-2xl font-bold text-slate-950 dark:text-slate-100">Rs.{stats.totalSpent.toFixed(2)}</div>
              </div>
            </div>
            </div>
            </div>
          </div>
        </div>
      </div>

      {/* Profile Content */}
      <div className="relative z-10 mx-auto max-w-4xl px-4 py-5 sm:px-6 lg:px-8 md:max-w-7xl md:py-8">
        <div className="space-y-6">
          <div className="space-y-5 md:hidden">
            <div>
              <div className="mb-3 px-1">
                <p className="text-[10px] font-semibold uppercase tracking-[0.22em] text-slate-400 dark:text-slate-500">Overview</p>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="rounded-[24px] bg-white px-4 py-4 shadow-[0_18px_35px_-28px_rgba(15,23,42,0.15)] dark:bg-slate-900 dark:ring-1 dark:ring-slate-800">
                  <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-400 dark:text-slate-500">Status</p>
                  <p className="mt-2 text-base font-black text-[#0f766e] dark:text-emerald-300">Verified</p>
                  <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">Ready for booking</p>
                </div>
                <div className="rounded-[24px] bg-white px-4 py-4 shadow-[0_18px_35px_-28px_rgba(15,23,42,0.15)] dark:bg-slate-900 dark:ring-1 dark:ring-slate-800">
                  <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-400 dark:text-slate-500">Pending</p>
                  <p className="mt-2 text-base font-black text-slate-950 dark:text-slate-100">{stats.pending}</p>
                  <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">Trips in progress</p>
                </div>
              </div>
            </div>
          </div>

            <div className="md:hidden">
              <div className="mb-3 px-1">
                <p className="text-[10px] font-semibold uppercase tracking-[0.22em] text-slate-400 dark:text-slate-500">Account Details</p>
              </div>
              <div className="overflow-hidden rounded-[28px] bg-white shadow-[0_18px_35px_-28px_rgba(15,23,42,0.15)] dark:bg-slate-900 dark:ring-1 dark:ring-slate-800">
                <div className="divide-y divide-slate-100 dark:divide-slate-800">
                <div className="px-4 py-4">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-400 dark:text-slate-500">Username</p>
                      {editingField === 'username' ? (
                        <div className="mt-3 space-y-2">
                          <input
                            type="text"
                            value={editFormData.username}
                            onChange={(e) => setEditFormData({ ...editFormData, username: e.target.value })}
                            className="w-full rounded-2xl border border-primary-500 bg-[#f7faf9] px-4 py-3 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:bg-slate-800 dark:text-slate-100"
                            placeholder="Enter username"
                          />
                          <div className="grid grid-cols-2 gap-2">
                            <button
                              onClick={handleSaveEdit}
                              disabled={updating}
                              className="rounded-2xl bg-[#0f766e] px-4 py-3 text-sm font-semibold text-white disabled:opacity-50"
                            >
                              {updating ? 'Saving...' : 'Save'}
                            </button>
                            <button
                              onClick={handleCancelEdit}
                              disabled={updating}
                              className="rounded-2xl bg-slate-200 px-4 py-3 text-sm font-semibold text-slate-700 disabled:opacity-50 dark:bg-slate-800 dark:text-slate-200"
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      ) : (
                        <p className="mt-2 truncate text-base font-semibold text-slate-950 dark:text-slate-100">{user?.username || 'Not provided'}</p>
                      )}
                    </div>
                    {editingField !== 'username' && (
                      <button
                        onClick={() => handleEditClick('username')}
                        className="flex h-10 w-10 items-center justify-center rounded-2xl bg-[#eef6f3] text-[#0f766e] dark:bg-emerald-500/12 dark:text-emerald-300"
                        title="Edit username"
                      >
                        <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                        </svg>
                      </button>
                    )}
                  </div>
                </div>
                <div className="px-4 py-4">
                  <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-400 dark:text-slate-500">Email</p>
                  <p className="mt-2 truncate text-base font-semibold text-slate-950 dark:text-slate-100">{user?.email || 'Not provided'}</p>
                </div>
                <div className="px-4 py-4">
                  <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-400 dark:text-slate-500">Phone</p>
                  <p className="mt-2 text-base font-semibold text-slate-950 dark:text-slate-100">{user?.phone || 'Not provided'}</p>
                </div>
                <div className="px-4 py-4">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-400 dark:text-slate-500">Pincode</p>
                      {editingField === 'pincode' ? (
                        <div className="mt-3 space-y-2">
                          <input
                            type="text"
                            value={editFormData.pincode}
                            onChange={(e) => setEditFormData({ ...editFormData, pincode: e.target.value })}
                            className="w-full rounded-2xl border border-primary-500 bg-[#f7faf9] px-4 py-3 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:bg-slate-800 dark:text-slate-100"
                            placeholder="Enter pincode"
                            maxLength="6"
                          />
                          <div className="grid grid-cols-2 gap-2">
                            <button
                              onClick={handleSaveEdit}
                              disabled={updating}
                              className="rounded-2xl bg-[#0f766e] px-4 py-3 text-sm font-semibold text-white disabled:opacity-50"
                            >
                              {updating ? 'Saving...' : 'Save'}
                            </button>
                            <button
                              onClick={handleCancelEdit}
                              disabled={updating}
                              className="rounded-2xl bg-slate-200 px-4 py-3 text-sm font-semibold text-slate-700 disabled:opacity-50 dark:bg-slate-800 dark:text-slate-200"
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      ) : (
                        <p className="mt-2 text-base font-semibold text-slate-950 dark:text-slate-100">{user?.pincode || 'Not provided'}</p>
                      )}
                    </div>
                    {editingField !== 'pincode' && (
                      <button
                        onClick={() => handleEditClick('pincode')}
                        className="flex h-10 w-10 items-center justify-center rounded-2xl bg-[#eef6f3] text-[#0f766e] dark:bg-emerald-500/12 dark:text-emerald-300"
                        title="Edit pincode"
                      >
                        <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                        </svg>
                      </button>
                    )}
                  </div>
                </div>
                <div className="px-4 py-4">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-400 dark:text-slate-500">Address</p>
                      {editingField === 'address' ? (
                        <div className="mt-3 space-y-2">
                          <textarea
                            value={editFormData.address}
                            onChange={(e) => setEditFormData({ ...editFormData, address: e.target.value })}
                            className="w-full rounded-2xl border border-primary-500 bg-[#f7faf9] px-4 py-3 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:bg-slate-800 dark:text-slate-100"
                            placeholder="Enter address"
                            rows="3"
                          />
                          <div className="grid grid-cols-2 gap-2">
                            <button
                              onClick={handleSaveEdit}
                              disabled={updating}
                              className="rounded-2xl bg-[#0f766e] px-4 py-3 text-sm font-semibold text-white disabled:opacity-50"
                            >
                              {updating ? 'Saving...' : 'Save'}
                            </button>
                            <button
                              onClick={handleCancelEdit}
                              disabled={updating}
                              className="rounded-2xl bg-slate-200 px-4 py-3 text-sm font-semibold text-slate-700 disabled:opacity-50 dark:bg-slate-800 dark:text-slate-200"
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      ) : (
                        <p className="mt-2 text-base font-semibold leading-6 text-slate-950 dark:text-slate-100">{user?.address || 'Not provided'}</p>
                      )}
                    </div>
                    {editingField !== 'address' && (
                      <button
                        onClick={() => handleEditClick('address')}
                        className="flex h-10 w-10 items-center justify-center rounded-2xl bg-[#eef6f3] text-[#0f766e] dark:bg-emerald-500/12 dark:text-emerald-300"
                        title="Edit address"
                      >
                        <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                        </svg>
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div className="md:hidden">
              <div className="mb-3 px-1">
                <p className="text-[10px] font-semibold uppercase tracking-[0.22em] text-slate-400 dark:text-slate-500">Travel Snapshot</p>
              </div>
              <div className="rounded-[28px] bg-white p-4 shadow-[0_18px_35px_-28px_rgba(15,23,42,0.15)] dark:bg-slate-900 dark:ring-1 dark:ring-slate-800">
                <div className="grid grid-cols-2 gap-3">
                  <div className="rounded-[22px] bg-[#eef6ff] p-4 dark:bg-indigo-500/10 dark:ring-1 dark:ring-indigo-900/30">
                    <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-blue-500 dark:text-indigo-300">Completed</p>
                    <p className="mt-2 text-xl font-black text-slate-950 dark:text-slate-100">{stats.completed}</p>
                  </div>
                  <div className="rounded-[22px] bg-[#f8f4f2] p-4 dark:bg-amber-500/10 dark:ring-1 dark:ring-amber-900/30">
                    <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-amber-500 dark:text-amber-300">Pending</p>
                    <p className="mt-2 text-xl font-black text-slate-950 dark:text-slate-100">{stats.pending}</p>
                  </div>
                </div>
                <div className="mt-3 rounded-[24px] bg-[#101827] px-4 py-4 text-white shadow-[0_18px_32px_-24px_rgba(15,23,42,0.6)]">
                  <p className="text-[10px] font-semibold uppercase tracking-[0.22em] text-white/45">Lifetime Spend</p>
                  <p className="mt-2 text-2xl font-black">Rs.{stats.totalSpent.toFixed(2)}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Personal Information */}
          <div className="hidden md:block rounded-[28px] border border-white/70 bg-white/94 p-5 shadow-[0_20px_40px_-30px_rgba(15,23,42,0.18)] dark:border-slate-800 dark:bg-slate-900/94 dark:text-slate-100 md:rounded-[32px] md:border-[#d9e8e2] md:bg-white/88 md:p-7 md:shadow-[0_30px_70px_-48px_rgba(15,23,42,0.45)] md:dark:border-slate-800 md:dark:bg-slate-900/88">
            <h2 className="mb-4 flex items-center text-lg font-bold text-gray-900 dark:text-slate-100 md:mb-6 md:text-xl">
              <svg className="w-5 h-5 md:w-6 md:h-6 text-primary-600 mr-2 md:mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
              Personal Information
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
              <div>
                <label className="mb-1.5 block text-xs font-medium text-gray-700 dark:text-slate-300 md:mb-2 md:text-sm">
                  Username
                </label>
                {editingField === 'username' ? (
                  <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
                    <input
                      type="text"
                      value={editFormData.username}
                      onChange={(e) => setEditFormData({ ...editFormData, username: e.target.value })}
                      className="flex-1 px-3 py-2 text-sm md:text-base border border-primary-500 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                      placeholder="Enter username"
                    />
                    <div className="flex gap-2">
                      <button
                        onClick={handleSaveEdit}
                        disabled={updating}
                        className="flex-1 sm:flex-none px-3 py-2 text-sm md:text-base bg-green-500 text-white rounded-lg hover:bg-green-600 disabled:opacity-50 whitespace-nowrap"
                      >
                        {updating ? 'Saving...' : 'Save'}
                      </button>
                      <button
                        onClick={handleCancelEdit}
                        disabled={updating}
                        className="flex-1 sm:flex-none px-3 py-2 text-sm md:text-base bg-gray-500 text-white rounded-lg hover:bg-gray-600 disabled:opacity-50 whitespace-nowrap"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center justify-between gap-2 rounded-lg border bg-gray-50 p-2.5 dark:border-slate-700 dark:bg-slate-800 md:p-3">
                    <span className="min-w-0 flex-1 truncate text-sm text-gray-900 dark:text-slate-100 md:text-base">{user?.username || 'Not provided'}</span>
                    <button
                      onClick={() => handleEditClick('username')}
                      className="flex-shrink-0 p-1.5 text-gray-400 transition-colors hover:text-primary-600 dark:text-slate-500 dark:hover:text-primary-400 md:p-1"
                      title="Edit username"
                    >
                      <svg className="w-4 h-4 md:w-4 md:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                      </svg>
                    </button>
                  </div>
                )}
              </div>
              
              <div>
                <label className="mb-1.5 block text-xs font-medium text-gray-700 dark:text-slate-300 md:mb-2 md:text-sm">
                  Email Address
                </label>
                <div className="flex items-center justify-between rounded-lg border bg-gray-50 p-2.5 dark:border-slate-700 dark:bg-slate-800 md:p-3">
                  <span className="min-w-0 flex-1 truncate text-sm text-gray-900 dark:text-slate-100 md:text-base">{user?.email || 'Not provided'}</span>
                </div>
              </div>
              
              <div>
                <label className="mb-1.5 block text-xs font-medium text-gray-700 dark:text-slate-300 md:mb-2 md:text-sm">
                  Phone Number
                </label>
                <div className="flex items-center justify-between rounded-lg border bg-gray-50 p-2.5 dark:border-slate-700 dark:bg-slate-800 md:p-3">
                  <span className="notranslate text-sm text-gray-900 dark:text-slate-100 md:text-base" translate="no">{user?.phone || 'Not provided'}</span>
                </div>
              </div>
              
              <div>
                <label className="mb-1.5 block text-xs font-medium text-gray-700 dark:text-slate-300 md:mb-2 md:text-sm">
                  Pincode
                </label>
                {editingField === 'pincode' ? (
                  <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
                    <input
                      type="text"
                      value={editFormData.pincode}
                      onChange={(e) => setEditFormData({ ...editFormData, pincode: e.target.value })}
                      className="flex-1 px-3 py-2 text-sm md:text-base border border-primary-500 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                      placeholder="Enter pincode"
                      maxLength="6"
                    />
                    <div className="flex gap-2">
                      <button
                        onClick={handleSaveEdit}
                        disabled={updating}
                        className="flex-1 sm:flex-none px-3 py-2 text-sm md:text-base bg-green-500 text-white rounded-lg hover:bg-green-600 disabled:opacity-50 whitespace-nowrap"
                      >
                        {updating ? 'Saving...' : 'Save'}
                      </button>
                      <button
                        onClick={handleCancelEdit}
                        disabled={updating}
                        className="flex-1 sm:flex-none px-3 py-2 text-sm md:text-base bg-gray-500 text-white rounded-lg hover:bg-gray-600 disabled:opacity-50 whitespace-nowrap"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center justify-between gap-2 rounded-lg border bg-gray-50 p-2.5 dark:border-slate-700 dark:bg-slate-800 md:p-3">
                    <span className="min-w-0 flex-1 truncate text-sm text-gray-900 dark:text-slate-100 md:text-base">{user?.pincode || 'Not provided'}</span>
                    <button
                      onClick={() => handleEditClick('pincode')}
                      className="flex-shrink-0 p-1.5 text-gray-400 transition-colors hover:text-primary-600 dark:text-slate-500 dark:hover:text-primary-400 md:p-1"
                      title="Edit pincode"
                    >
                      <svg className="w-4 h-4 md:w-4 md:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                      </svg>
                    </button>
                  </div>
                )}
              </div>
            </div>
            
            <div className="mt-4 md:mt-6">
              <label className="mb-1.5 block text-xs font-medium text-gray-700 dark:text-slate-300 md:mb-2 md:text-sm">
                Address
              </label>
              {editingField === 'address' ? (
                <div className="flex flex-col sm:flex-row items-stretch sm:items-start gap-2">
                  <textarea
                    value={editFormData.address}
                    onChange={(e) => setEditFormData({ ...editFormData, address: e.target.value })}
                    className="flex-1 px-3 py-2 text-sm md:text-base border border-primary-500 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                    placeholder="Enter address"
                    rows="3"
                  />
                  <div className="flex sm:flex-col gap-2">
                    <button
                      onClick={handleSaveEdit}
                      disabled={updating}
                      className="flex-1 sm:flex-none px-3 py-2 text-sm md:text-base bg-green-500 text-white rounded-lg hover:bg-green-600 disabled:opacity-50 whitespace-nowrap"
                    >
                      {updating ? 'Saving...' : 'Save'}
                    </button>
                    <button
                      onClick={handleCancelEdit}
                      disabled={updating}
                      className="flex-1 sm:flex-none px-3 py-2 text-sm md:text-base bg-gray-500 text-white rounded-lg hover:bg-gray-600 disabled:opacity-50 whitespace-nowrap"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <div className="flex items-start justify-between gap-2 rounded-lg border bg-gray-50 p-2.5 dark:border-slate-700 dark:bg-slate-800 md:p-3">
                  <span className="min-w-0 flex-1 break-words text-sm text-gray-900 dark:text-slate-100 md:text-base">{user?.address || 'Not provided'}</span>
                  <button
                    onClick={() => handleEditClick('address')}
                    className="flex-shrink-0 p-1.5 text-gray-400 transition-colors hover:text-primary-600 dark:text-slate-500 dark:hover:text-primary-400 md:p-1"
                    title="Edit address"
                  >
                    <svg className="w-4 h-4 md:w-4 md:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                    </svg>
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Account Status */}
          <div className="hidden md:block rounded-[28px] border border-white/70 bg-white/94 p-5 shadow-[0_20px_40px_-30px_rgba(15,23,42,0.18)] dark:border-slate-800 dark:bg-slate-900/94 dark:text-slate-100 md:rounded-[32px] md:border-[#d9e8e2] md:bg-white/88 md:p-6 md:shadow-[0_30px_70px_-48px_rgba(15,23,42,0.45)] md:dark:border-slate-800 md:dark:bg-slate-900/88">
            <h2 className="mb-6 flex items-center text-xl font-bold text-gray-900 dark:text-slate-100">
              <svg className="w-6 h-6 text-green-600 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              Account Status
            </h2>
            
            <div className="flex flex-col gap-3 rounded-[22px] border border-green-200 bg-green-50 p-4 dark:border-green-900/40 dark:bg-green-500/10 md:flex-row md:items-center md:justify-between md:rounded-[24px] md:bg-[linear-gradient(135deg,#ecfdf5_0%,#f4fbf7_100%)] md:px-5 md:py-5 md:dark:bg-[linear-gradient(135deg,#052e2b_0%,#0b3f3b_100%)]">
              <div className="flex items-center">
                <svg className="w-6 h-6 text-green-600 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <div>
                  <p className="font-medium text-green-800 dark:text-green-200">Account Verified</p>
                  <p className="text-sm text-green-600 dark:text-green-300">Your account is active and verified</p>
                </div>
              </div>
              <span className="rounded-full bg-green-100 px-3 py-1 text-sm font-medium text-green-800 dark:bg-green-500/15 dark:text-green-200">
                Active
              </span>
            </div>
          </div>

          {/* Ride Statistics */}
          <div className="hidden md:block rounded-[28px] border border-white/70 bg-white/94 p-5 shadow-[0_20px_40px_-30px_rgba(15,23,42,0.18)] dark:border-slate-800 dark:bg-slate-900/94 dark:text-slate-100 md:rounded-[32px] md:border-[#d9e8e2] md:bg-white/88 md:p-6 md:shadow-[0_30px_70px_-48px_rgba(15,23,42,0.45)] md:dark:border-slate-800 md:dark:bg-slate-900/88">
            <h2 className="mb-6 flex items-center text-xl font-bold text-gray-900 dark:text-slate-100">
              <svg className="w-6 h-6 text-blue-600 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
              Ride Statistics
            </h2>
            
            <div className="grid grid-cols-2 gap-4 md:grid-cols-4 md:gap-3">
              <div className="rounded-lg border-2 border-blue-200 bg-gradient-to-br from-blue-50 to-blue-100 p-4 text-center dark:border-blue-900/40 dark:from-slate-900 dark:to-slate-900 md:rounded-[24px] md:border md:border-[#d9e8e2] md:bg-[#f8fbfa] md:dark:border-slate-800 md:dark:bg-slate-950">
                <div className="text-2xl font-bold text-blue-600 dark:text-blue-300 md:text-[2rem] md:text-slate-950 md:dark:text-slate-100">{stats.total}</div>
                <div className="text-sm font-medium text-blue-800 dark:text-blue-200 md:mt-1 md:text-[11px] md:uppercase md:tracking-[0.2em] md:text-slate-400 md:dark:text-slate-500">Total Rides</div>
              </div>
              <div className="rounded-lg border-2 border-green-200 bg-gradient-to-br from-green-50 to-green-100 p-4 text-center dark:border-green-900/40 dark:from-slate-900 dark:to-slate-900 md:rounded-[24px] md:border md:border-[#d9e8e2] md:bg-[#f8fbfa] md:dark:border-slate-800 md:dark:bg-slate-950">
                <div className="text-2xl font-bold text-green-600 dark:text-green-300 md:text-[2rem] md:text-[#0f766e]">{stats.completed}</div>
                <div className="text-sm font-medium text-green-800 dark:text-green-200 md:mt-1 md:text-[11px] md:uppercase md:tracking-[0.2em] md:text-slate-400 md:dark:text-slate-500">Completed</div>
              </div>
              <div className="rounded-lg border-2 border-yellow-200 bg-gradient-to-br from-yellow-50 to-yellow-100 p-4 text-center dark:border-yellow-900/40 dark:from-slate-900 dark:to-slate-900 md:rounded-[24px] md:border md:border-[#d9e8e2] md:bg-[#f8fbfa] md:dark:border-slate-800 md:dark:bg-slate-950">
                <div className="text-2xl font-bold text-yellow-600 dark:text-yellow-300 md:text-[2rem] md:text-[#9a6700]">{stats.pending}</div>
                <div className="text-sm font-medium text-yellow-800 dark:text-yellow-200 md:mt-1 md:text-[11px] md:uppercase md:tracking-[0.2em] md:text-slate-400 md:dark:text-slate-500">Pending</div>
              </div>
              <div className="rounded-lg border-2 border-purple-200 bg-gradient-to-br from-purple-50 to-purple-100 p-4 text-center dark:border-purple-900/40 dark:from-slate-900 dark:to-slate-900 md:rounded-[24px] md:border md:border-[#d9e8e2] md:bg-[#f8fbfa] md:dark:border-slate-800 md:dark:bg-slate-950">
                <div className="text-2xl font-bold text-purple-600 dark:text-purple-300 md:text-[2rem] md:text-slate-100">Rs.{stats.totalSpent.toFixed(2)}</div>
                <div className="text-sm font-medium text-purple-800 dark:text-purple-200 md:mt-1 md:text-[11px] md:uppercase md:tracking-[0.2em] md:text-slate-400 md:dark:text-slate-500">Total Spent</div>
              </div>
            </div>
          </div>


          {/* Quick Actions */}
          <div className="hidden md:block rounded-[28px] border border-white/70 bg-white/94 p-5 shadow-[0_20px_40px_-30px_rgba(15,23,42,0.18)] dark:border-slate-800 dark:bg-slate-900/94 dark:text-slate-100 md:rounded-[32px] md:border-[#d9e8e2] md:bg-white/88 md:p-6 md:shadow-[0_30px_70px_-48px_rgba(15,23,42,0.45)] md:dark:border-slate-800 md:dark:bg-slate-900/88">
            <h2 className="mb-6 flex items-center text-xl font-bold text-gray-900 dark:text-slate-100">
              <svg className="w-6 h-6 text-primary-600 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
              Quick Actions
            </h2>
            
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <button className="flex items-center rounded-[22px] border border-primary-200 bg-primary-50 p-4 transition-colors hover:bg-primary-100 dark:border-emerald-900/40 dark:bg-emerald-500/10 dark:hover:bg-emerald-500/15 md:rounded-[24px] md:bg-[linear-gradient(135deg,#ecfdf5_0%,#f0faf7_100%)] md:dark:bg-[linear-gradient(135deg,#052e2b_0%,#0b3f3b_100%)]">
                <svg className="w-6 h-6 text-primary-600 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                </svg>
                <div className="text-left">
                  <p className="font-medium text-primary-800">Edit Profile</p>
                  <p className="text-sm text-primary-600">Update your information</p>
                </div>
              </button>
              
              <button className="flex items-center rounded-[22px] border border-green-200 bg-green-50 p-4 transition-colors hover:bg-green-100 dark:border-green-900/40 dark:bg-green-500/10 dark:hover:bg-green-500/15 md:rounded-[24px] md:bg-[linear-gradient(135deg,#effcf4_0%,#f4fbf7_100%)] md:dark:bg-[linear-gradient(135deg,#052e2b_0%,#0b3f3b_100%)]">
                <svg className="w-6 h-6 text-green-600 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
                <div className="text-left">
                  <p className="font-medium text-green-800">Ride History</p>
                  <p className="text-sm text-green-600">View past rides</p>
                </div>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Non-blocking Toast Notification - Appears automatically, disappears after 2.5s */}
      {messageModal.show && (
        <div 
          className="fixed w-[90%] max-w-md animate-slideDown"
          style={{ 
            zIndex: 999999,
            position: 'fixed',
            top: '80px',
            left: '50%',
            transform: 'translateX(-50%)',
            pointerEvents: 'auto'
          }}
          role="alert"
          aria-live="polite"
        >
          <div
            className={`px-5 py-4 rounded-xl shadow-2xl flex items-center gap-3 text-white border-2 ${
              messageModal.type === 'success'
                ? 'bg-green-600 border-green-400'
                : messageModal.type === 'error'
                ? 'bg-red-600 border-red-400'
                : messageModal.type === 'warning'
                ? 'bg-orange-600 border-orange-400'
                : 'bg-blue-600 border-blue-400'
            }`}
            style={{ boxShadow: '0 10px 40px rgba(0,0,0,0.3)' }}
          >
            {/* Success Icon */}
            {messageModal.type === 'success' && (
              <svg className="w-6 h-6 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
              </svg>
            )}
            {/* Error Icon */}
            {messageModal.type === 'error' && (
              <svg className="w-6 h-6 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" />
              </svg>
            )}
            {/* Message Text */}
            <div className="flex-1">
              {messageModal.title && (
                <div className="text-base font-bold mb-1">{messageModal.title}</div>
              )}
              <div className="text-base font-semibold">{messageModal.message}</div>
            </div>
            {/* Optional Close Button (doesn't block, just allows manual dismiss) */}
            <button
              type="button"
              onClick={() => setMessageModal({ show: false, message: '', type: 'info', title: '' })}
              className="text-white/80 hover:text-white flex-shrink-0 ml-1 transition-opacity"
              aria-label="Close notification"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
      )}

      {/* Message Modal - Only show for errors/warnings, not for success */}
      {messageModal.show && messageModal.type !== 'success' && (
        <MessageModal
          message={messageModal.message}
          title={messageModal.title}
          type={messageModal.type}
          onClose={() => setMessageModal({ show: false, message: '', type: 'info', title: 'Message' })}
        />
      )}
    </div>
    </>
  );
};

export default Profile;
